"use client"

import styled from "@emotion/styled"
import { Button } from "@mui/material"
import { useTranslations } from "next-intl"
import { Dispatch, SetStateAction } from "react"
import NewText from "./NewText/NewText"
import { useIsDesktop } from "./common/bit/MediaQueries"
import { FormFailIcon, FormFailNewIcon, FormSuccessIcon, FormSuccessNewIcon } from "./icons"
import { sharedColors as colors } from "./theme/colors"

const StyledForm = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  min-height: 400px;
  text-align: center;
  ${(props) => props.theme.breakpoints.up("md")} {
    min-height: 400px;
  }

  &.newStyle {
    height: 100%;
  }
`
const StyledText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const StyledButton = styled(Button)`
  display: flex;
  width: 153px;
  height: 48px;
  padding: 8px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 200px;
  border: 2px solid black;
  background: white;
  text-decoration: none;
  font-size: 18px;
  font-weight: 600;
  :hover {
    text-decoration: none;
  }
`

const NewStyledButton = styled(Button)<{ desktopMinWidth?: string }>`
  --theme-color: ${(props) => props.theme.colors.blue4rentMarketing};
  border-radius: 8px;
  font-weight: 700;
  font-size: 20px;
  line-height: 24px;
  letter-spacing: 0px;
  padding: 12px 5px;
  justify-content: center;
  min-width: 200px;

  color: white;
  background-color: var(--theme-color);
  border-color: var(--theme-color);
  width: 100%;
  &[disabled] {
    color: white;
    background-color: ${(props) => props.theme.colors.gray4rentMarketing};
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    width: unset;
    min-width: ${(props) => props.desktopMinWidth || "200px"};
  }

  &:hover {
    background-color: ${(props) => props.theme.colors.blue4rentMarketingHover};
  }
`

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #d756671a;
  svg {
    width: 40px;
    height: 40px;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 98px;
    height: 98px;
    margin-bottom: 24px;
    svg {
      width: 66px;
      height: 66px;
    }
  }
`

const NewStyleWrapper = styled.div`
  display: flex;
  height: 100%;
  flex: 1;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`

type Props = {
  state: "success" | "fail" | ""
  setIsSubmitted: Dispatch<SetStateAction<boolean>>
  messages: any
  type:
    | "contactUs"
    | "project"
    | "career"
    | "contact-marketing-4-rent"
    | "payment-marketing-4-rent"
    | "rent"
  onClose?: any
  newStyle?: boolean
}
function SuccessFailState({
  state,
  setIsSubmitted,
  messages,
  type,
  onClose = () => {},
  newStyle,
}: Props) {
  const isDesktop = useIsDesktop()
  const t = useTranslations("SuccessFailBox")
  const getMessageTitle = () => {
    if (state === "success") {
      switch (type) {
        case "career":
          return t("successCareer")
        case "contactUs":
          return t("successContactUs")
        case "project":
          return t("successProject")
        case "contact-marketing-4-rent":
        case "rent":
          return t("projectRent4ContactSuccessTitle")
        case "payment-marketing-4-rent":
          return t("projectRent4PaymentSuccessTitle")
        default:
          return ""
      }
    } else {
      switch (type) {
        case "career":
          return t("errorCareer")
        case "contactUs":
          return t("errorContactUs")
        case "project":
          return t("errorProject")
        case "contact-marketing-4-rent":
        case "rent":
          return t("projectRent4ContactErrorTitle")
        case "payment-marketing-4-rent":
          return t("projectRent4PaymentErrorTitle")
        default:
          return ""
      }
    }
  }
  const getMessageText = () => {
    if (state === "success") {
      switch (type) {
        case "contact-marketing-4-rent":
        case "rent":
          return t("projectRent4ContactSuccessMsg")
        case "payment-marketing-4-rent":
          return t("projectRent4PaymentSuccessMsg")
        default:
          return ""
      }
    } else {
      switch (type) {
        case "contact-marketing-4-rent":
        case "rent":
          return t("projectRent4ContactErrorMsg")
        case "payment-marketing-4-rent":
          return t("projectRent4PaymentErrorMsg")
        default:
          return ""
      }
    }
  }
  return (
    <StyledForm className={newStyle ? "newStyle" : ""}>
      {newStyle ? (
        <>
          <NewStyleWrapper>
            <IconWrapper style={{ backgroundColor: state === "success" ? "#E3F8E6" : "#D756671A" }}>
              {state === "success" ? (
                <FormSuccessNewIcon color={"#008015"} />
              ) : (
                <FormFailNewIcon color={"#C41407"} />
              )}
            </IconWrapper>
            <StyledText style={{ paddingBottom: isDesktop ? 40 : "" }}>
              <NewText
                component={"p"}
                desktopFontSize={32}
                desktopLineHeight={"130%"}
                desktopFontWeight={400}
                mobileFontWeight={500}
                mobileLineHeight={"150%"}
                sx={{
                  margin: 0,
                }}
                color={colors.black}
              >
                {getMessageTitle()}
              </NewText>
              <NewText
                component={"p"}
                desktopFontSize={18}
                desktopLineHeight={"150%"}
                fontWeight={300}
                mobileLineHeight={"145%"}
                sx={{
                  margin: 0,
                }}
                color={colors.darkBlue4rentMarketing}
              >
                {getMessageText()}
              </NewText>
            </StyledText>
            {isDesktop &&
              (state === "fail" ? (
                <NewStyledButton
                  variant="outlined"
                  onClick={() => setIsSubmitted(false)}
                  desktopMinWidth={type === "project" ? "335px" : "200px"}
                >
                  {t("projectRent4AnotherAttempt")}
                </NewStyledButton>
              ) : (
                <NewStyledButton
                  variant="outlined"
                  onClick={() => onClose()}
                  desktopMinWidth={type === "project" ? "335px" : "200px"}
                >
                  {t("projectRent4ContactSuccessBtn")}
                </NewStyledButton>
              ))}
          </NewStyleWrapper>
          {!isDesktop &&
            (state === "fail" ? (
              <NewStyledButton variant="outlined" onClick={() => setIsSubmitted(false)}>
                {t("projectRent4AnotherAttempt")}
              </NewStyledButton>
            ) : (
              <NewStyledButton variant="outlined" onClick={() => onClose()}>
                {t("projectRent4ContactSuccessBtn")}
              </NewStyledButton>
            ))}
        </>
      ) : (
        <>
          {state === "success" ? <FormSuccessIcon /> : <FormFailIcon />}
          <StyledText>
            {/* <NewText component={"h3"} desktopFontSize={32} color={colors.black}>
          {state === "success"
            ? type === "rent"
              ? messages.successTitleRent
              : messages.sucessTitle
            : "פנייתך לא נשלחה "}
        </NewText> */}
            {/* <NewText component={"p"} desktopFontSize={18} color={colors.black} maxWidth={"400px"}>
          {state === "success"
            ? type === "rent"
              ? messages.successTextRent
              : messages.sucessText
            : `לורם איפסום דולר לורם איפסום דולר
          לורם איפסום דולר לורם איפסום`}
        </NewText> */}
            <NewText
              component={"p"}
              desktopFontSize={18}
              color={colors.black}
              maxWidth={"400px"}
              dangerouslySetInnerHTML={{ __html: getMessageTitle() }}
            />
          </StyledText>
          {state === "fail" ? (
            <StyledButton onClick={() => setIsSubmitted(false)}>ניסיון נוסף</StyledButton>
          ) : (
            <StyledButton onClick={() => onClose()}>{messages.close}</StyledButton>
          )}
        </>
      )}
    </StyledForm>
  )
}

export default SuccessFailState
